import React, { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

interface ApiResponse {
  success: boolean;
  data: Course[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface CourseChipsProps {
  onCourseSelect?: (course: Course) => void;
  selectedCourseId?: string;
  showSearch?: boolean;
  maxDisplay?: number;
}

const CourseChips: React.FC<CourseChipsProps> = ({
  onCourseSelect,
  selectedCourseId,
  showSearch = true,
  maxDisplay
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: '1', limit: '100', status: 'active' });
      const response = await apiClient.get<ApiResponse>(`/v1/courses?${params}`);
      if (response.data.success) {
        setCourses(response.data.data);
      } else {
        setError('Failed to load courses');
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
      setError(err.response?.data?.error || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(search.toLowerCase()) ||
    course.title.toLowerCase().includes(search.toLowerCase())
  );

  const displayedCourses = maxDisplay
    ? filteredCourses.slice(0, maxDisplay)
    : filteredCourses;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '40px', background: 'rgba(14,34,93,0.03)',
        borderRadius: '12px', border: '1px solid #E5E7EB'
      }}>
        <ProgressSpinner style={{ width: '38px', height: '38px' }} strokeWidth="3" animationDuration=".8s" />
      </div>
    );
  }

  if (error) {
    return <Message severity="error" text={error} style={{ fontSize: '0.9rem', fontWeight: '500' }} />;
  }

  return (
    <div>

      {/* ── Search ───────────────────────────────────────────────────────── */}
      {showSearch && (
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <i className="pi pi-search" style={{
            position: 'absolute', left: '14px', top: '50%',
            transform: 'translateY(-50%)', color: '#9ca3af',
            fontSize: '0.9rem', pointerEvents: 'none'
          }}></i>
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courses..."
            style={{
              width: '100%', paddingLeft: '42px', height: '42px',
              border: '1px solid #E5E7EB', borderRadius: '10px',
              fontSize: '0.9rem', color: '#0e225d', outline: 'none',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#fbb21f';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(251,178,31,0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {displayedCourses.length === 0 ? (
        <div style={{
          padding: '40px 20px', textAlign: 'center',
          background: 'rgba(14,34,93,0.03)', borderRadius: '12px',
          border: '2px dashed #E5E7EB'
        }}>
          <div style={{
            width: '48px', height: '48px',
            background: 'rgba(14,34,93,0.06)', borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px'
          }}>
            <i className="pi pi-book" style={{ fontSize: '1.4rem', color: '#0e225d', opacity: 0.3 }}></i>
          </div>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>
            {search ? 'No courses found matching your search' : 'No courses available'}
          </p>
        </div>

      ) : (
        /* ── Chips ───────────────────────────────────────────────────────── */
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {displayedCourses.map((course) => {
            const isSelected = selectedCourseId === course.course_id;
            const isActive = course.status === 'active';

            return (
              <div
                key={course.course_id}
                onClick={() => onCourseSelect?.(course)}
                style={{
                  padding: '10px 18px',
                  background: isSelected
                    ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)'
                    : 'white',
                  border: isSelected ? '2px solid transparent' : '2px solid #E5E7EB',
                  borderRadius: '24px',
                  cursor: onCourseSelect ? 'pointer' : 'default',
                  transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  boxShadow: isSelected ? '0 4px 14px rgba(14,34,93,0.25)' : 'none',
                  position: 'relative', overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (onCourseSelect && !isSelected) {
                    e.currentTarget.style.background = 'rgba(14,34,93,0.04)';
                    e.currentTarget.style.borderColor = 'rgba(14,34,93,0.25)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,34,93,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (onCourseSelect && !isSelected) {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {/* Initial bubble */}
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: isSelected ? '#fbb21f' : 'rgba(14,34,93,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.2s ease'
                }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: '800', color: '#0e225d' }}>
                    {course.name.charAt(0).toUpperCase()}
                  </span>
                </div>

                {/* Course info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{
                    fontSize: '0.85rem', fontWeight: '600', lineHeight: '1.2',
                    color: isSelected ? 'white' : '#0e225d'
                  }}>
                    {course.name}
                  </span>
                  <span style={{
                    fontSize: '0.7rem', lineHeight: '1.2',
                    color: isSelected ? 'rgba(255,255,255,0.65)' : '#9ca3af'
                  }}>
                    {course.title}
                  </span>
                </div>

                {/* Status badge */}
                <div style={{
                  marginLeft: '4px', padding: '3px 9px',
                  borderRadius: '20px', fontSize: '0.63rem', fontWeight: '700',
                  letterSpacing: '0.4px', textTransform: 'capitalize',
                  ...(isActive
                    ? isSelected
                      ? { background: '#fbb21f', color: '#0e225d' }
                      : { background: 'rgba(22,163,74,0.1)', color: '#15803d', border: '1px solid rgba(22,163,74,0.25)' }
                    : { background: 'rgba(239,68,68,0.1)', color: '#c62828', border: '1px solid rgba(239,68,68,0.25)' }
                  )
                }}>
                  {course.status}
                </div>

                {/* Selected checkmark */}
                {isSelected && (
                  <i className="pi pi-check" style={{ fontSize: '0.75rem', color: '#fbb21f', marginLeft: '2px' }}></i>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Overflow note ─────────────────────────────────────────────────── */}
      {maxDisplay && filteredCourses.length > maxDisplay && (
        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <span style={{
            fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic',
            display: 'inline-flex', alignItems: 'center', gap: '4px'
          }}>
            <i className="pi pi-info-circle" style={{ fontSize: '0.72rem' }}></i>
            Showing {maxDisplay} of {filteredCourses.length} courses
          </span>
        </div>
      )}

    </div>
  );
};

export default CourseChips;


// import React, { useState, useEffect } from 'react';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import { Message } from 'primereact/message';
// import { InputText } from 'primereact/inputtext';
// import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

// interface Course {
//   course_id: string;
//   name: string;
//   title: string;
//   status: string;
// }

// interface ApiResponse {
//   success: boolean;
//   data: Course[];
//   pagination: {
//     total: number;
//     page: number;
//     limit: number;
//     pages: number;
//   };
// }

// interface CourseChipsProps {
//   onCourseSelect?: (course: Course) => void;
//   selectedCourseId?: string;
//   showSearch?: boolean;
//   maxDisplay?: number;
// }

// const CourseChips: React.FC<CourseChipsProps> = ({
//   onCourseSelect,
//   selectedCourseId,
//   showSearch = true,
//   maxDisplay
// }) => {
//   const [courses, setCourses] = useState<Course[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);
//   const [search, setSearch] = useState('');

//   useEffect(() => {
//     fetchCourses();
//   }, []);

//   const fetchCourses = async () => {
//     try {
//       setLoading(true);
//       setError(null);
      
//       const params = new URLSearchParams({
//         page: '1',
//         limit: '100'
//       });

//       const response = await apiClient.get<ApiResponse>(`/v1/courses?${params}`);
      
//       if (response.data.success) {
//         setCourses(response.data.data);
//       } else {
//         setError('Failed to load courses');
//       }
//     } catch (err: any) {
//       console.error('Failed to fetch courses:', err);
//       setError(err.response?.data?.error || 'Failed to load courses');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const filteredCourses = courses.filter(course =>
//     course.name.toLowerCase().includes(search.toLowerCase()) ||
//     course.title.toLowerCase().includes(search.toLowerCase())
//   );

//   const displayedCourses = maxDisplay 
//     ? filteredCourses.slice(0, maxDisplay) 
//     : filteredCourses;

//   if (loading) {
//     return (
//       <div style={{
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         padding: '40px',
//         background: '#f5f5f5',
//         borderRadius: '12px'
//       }}>
//         <ProgressSpinner 
//           style={{ width: '40px', height: '40px' }} 
//           strokeWidth="3"
//         />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <Message 
//         severity="error" 
//         text={error}
//         style={{ fontSize: '0.9rem', fontWeight: '500' }}
//       />
//     );
//   }

//   return (
//     <div>
//       {showSearch && (
//         <div style={{
//           marginBottom: '20px',
//           position: 'relative'
//         }}>
//           <i className="pi pi-search" style={{
//             position: 'absolute',
//             left: '14px',
//             top: '50%',
//             transform: 'translateY(-50%)',
//             color: '#999',
//             fontSize: '0.9rem'
//           }}></i>
//           <InputText
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             placeholder="Search courses..."
//             style={{
//               width: '100%',
//               paddingLeft: '42px',
//               height: '42px',
//               border: '2px solid #f0f0f0',
//               borderRadius: '10px',
//               fontSize: '0.9rem'
//             }}
//           />
//         </div>
//       )}

//       {displayedCourses.length === 0 ? (
//         <div style={{
//           padding: '40px 20px',
//           textAlign: 'center',
//           background: '#f5f5f5',
//           borderRadius: '12px',
//           border: '2px dashed #e0e0e0'
//         }}>
//           <i className="pi pi-book" style={{ fontSize: '2.5rem', color: '#ddd', marginBottom: '12px' }}></i>
//           <p style={{ margin: 0, color: '#999', fontSize: '0.9rem' }}>
//             {search ? 'No courses found matching your search' : 'No courses available'}
//           </p>
//         </div>
//       ) : (
//         <div style={{
//           display: 'flex',
//           flexWrap: 'wrap',
//           gap: '10px'
//         }}>
//           {displayedCourses.map((course) => (
//             <div
//               key={course.course_id}
//               onClick={() => onCourseSelect?.(course)}
//               style={{
//                 padding: '10px 18px',
//                 background: selectedCourseId === course.course_id 
//                   ? 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)' 
//                   : 'white',
//                 border: selectedCourseId === course.course_id 
//                   ? '2px solid #0f3d3e' 
//                   : '2px solid #e0e0e0',
//                 borderRadius: '24px',
//                 cursor: onCourseSelect ? 'pointer' : 'default',
//                 transition: 'all 0.2s ease',
//                 display: 'flex',
//                 alignItems: 'center',
//                 gap: '8px',
//                 position: 'relative',
//                 overflow: 'hidden'
//               }}
//               onMouseEnter={(e) => {
//                 if (onCourseSelect && selectedCourseId !== course.course_id) {
//                   e.currentTarget.style.background = '#f5f5f5';
//                   e.currentTarget.style.borderColor = '#0f3d3e';
//                   e.currentTarget.style.transform = 'translateY(-2px)';
//                   e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,61,62,0.15)';
//                 }
//               }}
//               onMouseLeave={(e) => {
//                 if (onCourseSelect && selectedCourseId !== course.course_id) {
//                   e.currentTarget.style.background = 'white';
//                   e.currentTarget.style.borderColor = '#e0e0e0';
//                   e.currentTarget.style.transform = 'translateY(0)';
//                   e.currentTarget.style.boxShadow = 'none';
//                 }
//               }}
//             >
//               {/* Icon */}
//               <div style={{
//                 width: '28px',
//                 height: '28px',
//                 borderRadius: '50%',
//                 background: selectedCourseId === course.course_id 
//                   ? '#d4f17e' 
//                   : '#0f3d3e',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 flexShrink: 0
//               }}>
//                 <span style={{
//                   fontSize: '0.8rem',
//                   fontWeight: '700',
//                   color: selectedCourseId === course.course_id 
//                     ? '#0f3d3e' 
//                     : '#d4f17e'
//                 }}>
//                   {course.name.charAt(0).toUpperCase()}
//                 </span>
//               </div>

//               {/* Course Info */}
//               <div style={{ 
//                 display: 'flex', 
//                 flexDirection: 'column',
//                 gap: '2px'
//               }}>
//                 <span style={{
//                   fontSize: '0.85rem',
//                   fontWeight: '600',
//                   color: selectedCourseId === course.course_id 
//                     ? 'white' 
//                     : '#1a1a1a',
//                   lineHeight: '1.2'
//                 }}>
//                   {course.name}
//                 </span>
//                 <span style={{
//                   fontSize: '0.7rem',
//                   color: selectedCourseId === course.course_id 
//                     ? 'rgba(255,255,255,0.8)' 
//                     : '#999',
//                   lineHeight: '1.2'
//                 }}>
//                   {course.title}
//                 </span>
//               </div>

//               {/* Status Badge */}
//               <div style={{
//                 marginLeft: '8px',
//                 padding: '3px 8px',
//                 borderRadius: '12px',
//                 fontSize: '0.65rem',
//                 fontWeight: '600',
//                 background: course.status === 'active' 
//                   ? (selectedCourseId === course.course_id ? '#d4f17e' : 'rgba(212, 241, 126, 0.2)')
//                   : 'rgba(255, 205, 210, 0.3)',
//                 color: course.status === 'active' 
//                   ? (selectedCourseId === course.course_id ? '#0f3d3e' : '#0f3d3e')
//                   : '#b71c1c',
//                 textTransform: 'capitalize'
//               }}>
//                 {course.status}
//               </div>

//               {/* Selected Indicator */}
//               {selectedCourseId === course.course_id && (
//                 <i className="pi pi-check" style={{
//                   fontSize: '0.8rem',
//                   color: '#d4f17e',
//                   marginLeft: '4px'
//                 }}></i>
//               )}
//             </div>
//           ))}
//         </div>
//       )}

//       {maxDisplay && filteredCourses.length > maxDisplay && (
//         <div style={{
//           marginTop: '12px',
//           textAlign: 'center'
//         }}>
//           <span style={{
//             fontSize: '0.8rem',
//             color: '#999',
//             fontStyle: 'italic'
//           }}>
//             Showing {maxDisplay} of {filteredCourses.length} courses
//           </span>
//         </div>
//       )}
//     </div>
//   );
// };

// export default CourseChips;